import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logoLight from "@/assets/mark-tognoli-realty-logo-light.png";
import logoDark from "@/assets/mark-tognoli-realty-logo.png";

const navItems = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Buy", href: "/buy" },
  { label: "Sell", href: "/sell" },
  { label: "Contact", href: "/contact" },
];

interface NavigationProps {
  /** When true, nav starts transparent over a hero and turns solid on scroll. */
  transparentOnTop?: boolean;
}

const Navigation = ({ transparentOnTop = false }: NavigationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(!transparentOnTop);
  const location = useLocation();

  useEffect(() => {
    if (!transparentOnTop) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentOnTop]);

  const isSolid = scrolled;
  const useLightLogo = transparentOnTop && !isSolid;

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        isSolid
          ? "bg-background/95 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto max-w-[1400px] px-6">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" aria-label="Mark Tognoli Realty home" className="flex items-center">
            <img
              src={useLightLogo ? logoLight : logoDark}
              alt="Mark Tognoli Realty — San Francisco Bay Area Luxury Real Estate"
              width={220}
              height={76}
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => {
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-[11px] tracking-[0.3em] uppercase font-medium transition-colors duration-300 ${
                    isSolid
                      ? active
                        ? "text-primary"
                        : "text-foreground hover:text-primary"
                      : active
                        ? "text-brand-ivory"
                        : "text-white/85 hover:text-brand-ivory"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <a
            href="tel:+14157106922"
            className={`hidden md:inline-block text-[11px] tracking-[0.3em] uppercase border px-5 py-2.5 transition-all duration-300 ${
              isSolid
                ? "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                : "border-white/40 text-white hover:bg-white hover:text-foreground"
            }`}
          >
            (415) 710-6922
          </a>

          <button
            className={`md:hidden p-2 ${isSolid ? "text-foreground" : "text-white"}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden bg-background border-t border-border py-6 space-y-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className="block text-sm tracking-[0.3em] uppercase text-foreground hover:text-primary"
              >
                {item.label}
              </Link>
            ))}
            <a
              href="tel:+14157106922"
              className="block text-sm tracking-[0.3em] uppercase text-primary"
            >
              (415) 710-6922
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
